/**
 * The live panel of the schedule on the tablet now (BA-85 C1–C6), on Controls in the
 * current-action slot. It shows what the tablet last reported in status/sentSchedule and
 * changes only when the node does. Remove confirms (BA-55) and makes the one DELETE.
 */

import React, { useLayoutEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Delete as DeleteIcon, Lock as LockIcon, HourglassEmpty as HourglassIcon, CloudOff } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Action } from '../types/action';
import { Schedule } from '../types/schedule';
import { SentSchedule } from '../types/sentSchedule';
import { SentScheduleApiError, sentScheduleService } from '../services/sentScheduleService';
import {
  currentStepOf,
  doneCount,
  formatTime,
  isChoiceBoard,
  isParentStep,
  lastUpdate,
  orderedSteps,
  stepPosition,
  waitingMinutes,
} from '../utils/sentSchedule';
import { useTapGuard } from '../hooks/useTapGuard';
import { RichText } from './RichText';
import { Fact, NameRow, StepList, StepPicture, Tokens, useStepPictures } from './sentSchedule/parts';
import { ActionSelector } from './ActionSelector';
import { SendActionDialog } from './SendActionDialog';

/** The Remove dialog's error line: an i18n key under sentSchedule.remove.* */
export type RemoveErrorKey = 'error' | 'forbidden' | 'server';

export function removeErrorKey(status: number): RemoveErrorKey | null {
  // 404 (nothing there) and 409 (already ended): what the parent wanted has happened (C12)
  if (status === 404 || status === 409) return null;
  if (status === 403) return 'forbidden';
  if (status >= 500) return 'server';
  return 'error';
}

interface SentSchedulePanelProps {
  node: SentSchedule;
  deviceId: string;
  deviceName: string;
  actions: Action[];
  schedules?: Schedule[];
  tabletOffline: boolean;
  lastSeen: number | null;
  online: boolean;
  now: number;
  onReloadActions?: () => void;
}

export const SentSchedulePanel: React.FC<SentSchedulePanelProps> = ({
  node,
  deviceId,
  deviceName,
  actions,
  schedules,
  tabletOffline,
  lastSeen,
  online,
  now,
  onReloadActions,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const lang = i18n.language;

  const steps = orderedSteps(node);
  const pictures = useStepPictures(deviceId, steps, actions);
  const choice = isChoiceBoard(node);
  const { n, total } = stepPosition(node);
  const step = currentStepOf(node);
  const done = doneCount(node);
  const parentStep = isParentStep(node, step);
  const minutes = waitingMinutes(node, step);
  const agoMin = Math.floor((now - node.sentAt) / 60000);
  const started = agoMin >= 1
    ? t('sentSchedule.startedVal', { time: formatTime(node.sentAt, lang, now), ago: agoMin })
    : t('sentSchedule.startedJustNow', { time: formatTime(node.sentAt, lang, now) });

  // Remove (C6/C6o/C12)
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<RemoveErrorKey | null>(null);
  const guard = useTapGuard();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const removeRequest = useRef(0);
  useLayoutEffect(() => {
    if (removeOpen) guard.arm();
  }, [removeOpen, guard]);
  // The dialog's offline wording follows the tablet while it is open; it is read when it opens
  const offlineWording = tabletOffline;

  const openRemove = () => {
    setRemoveError(null);
    setRemoveOpen(true);
  };
  const closeRemove = () => {
    if (!removing) setRemoveOpen(false);
  };
  const confirmRemove = async (event: React.MouseEvent) => {
    if (!guard.accept(event) || removing) return;
    const id = ++removeRequest.current;
    setRemoving(true);
    setRemoveError(null);
    let error: RemoveErrorKey | null = null;
    try {
      await sentScheduleService.remove(deviceId);
    } catch (err) {
      error = removeErrorKey(err instanceof SentScheduleApiError ? err.status : 0);
    }
    if (id !== removeRequest.current) return;
    setRemoving(false);
    if (error) {
      guard.arm();
      setRemoveError(error);
      cancelRef.current?.focus();
    } else {
      setRemoveOpen(false);
    }
  };
  React.useEffect(() => () => { removeRequest.current += 1; }, []);

  // Send an action instead: today's selector and dialog, with the replace warning (m5)
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [actionToSend, setActionToSend] = useState<Action | null>(null);

  // On a phone the design's header (C1b): items centred, 8 px apart, also when Remove wraps under the title
  const header = (
    <Box sx={{ display: 'flex', alignItems: isPhone ? 'center' : 'flex-start', justifyContent: 'space-between',
      gap: isPhone ? 1 : 2, flexWrap: 'wrap', mb: 2 }}>
      <Typography variant="h6" component="h2" sx={isPhone ? undefined : { pt: 0.5 }}>
        {t('sentSchedule.onTablet')}
      </Typography>
      <Button
        variant="outlined"
        color="error"
        startIcon={<DeleteIcon />}
        onClick={openRemove}
        disabled={!online}
        sx={isPhone ? { minHeight: 48, px: 2.5 } : undefined}
      >
        {t('currentAction.remove')}
      </Button>
    </Box>
  );

  const nowBox = choice ? (
    <Box sx={{ bgcolor: 'grey.100', borderRadius: 2, p: 2, mb: 2 }}>
      <Typography variant="body1" color="primary.main">{t('sentSchedule.choosing')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{t('sentSchedule.choosingHelp')}</Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {steps.map((s) => (
          <Box key={s.id} sx={{ width: 64, textAlign: 'center' }}>
            <StepPicture step={s} size={64} pictures={pictures} schedules={schedules} />
            <Typography variant="body2" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>{s.title}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  ) : (
    <Box sx={{ bgcolor: 'grey.100', borderRadius: 2, p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
      {step && <StepPicture step={step} size={isPhone ? 96 : 120} pictures={pictures} schedules={schedules} />}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body1" color="primary.main">{t('sentSchedule.stepOf', { n, total })}</Typography>
        <Typography variant="h5" component="p" sx={{ fontSize: isPhone ? 22 : 24, overflowWrap: 'anywhere' }}>
          {step?.title}
        </Typography>
        {parentStep && (
          <>
            <Chip
              icon={<LockIcon />}
              label={t('sentSchedule.parentStep')}
              color="warning"
              sx={{ mt: 1, height: 'auto', py: 0.5, '& .MuiChip-label': { whiteSpace: 'normal' } }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('sentSchedule.parentStepHelp')}
            </Typography>
          </>
        )}
        {minutes !== null && (
          <>
            <Chip
              icon={<HourglassIcon />}
              label={t('sentSchedule.waitingFor', { minutes })}
              // The timer length is the chip's point: it wraps on a phone instead of an ellipsis
              sx={{ mt: 1, height: 'auto', py: 0.5 }}
              slotProps={{ label: { sx: { whiteSpace: 'normal', textOverflow: 'clip' } } }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('sentSchedule.waitingHelp')}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );

  const facts = (
    <Box sx={{ display: 'grid', gridTemplateColumns: isPhone ? '1fr 1fr' : 'repeat(3, max-content)',
      columnGap: 5, rowGap: 2, mb: 2 }}>
      <Fact label={t('sentSchedule.tokens')}><Tokens count={node.tokensEarned ?? 0} /></Fact>
      <Fact label={t('sentSchedule.started')}>{started}</Fact>
      <Fact label={t('sentSchedule.lastUpdate')}>{formatTime(lastUpdate(node), lang, now)}</Fact>
    </Box>
  );

  const left = (
    <Box sx={{ minWidth: 0 }}>
      {nowBox}
      {!choice && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={total ? (done / total) * 100 : 0}
            aria-label={t('sentSchedule.doneOf', { done, total })}
            sx={{ height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { transition: 'none' } }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('sentSchedule.doneOf', { done, total })}
          </Typography>
        </Box>
      )}
      {facts}
    </Box>
  );

  return (
    <>
      <Card>
        <CardContent>
          {header}
          {!online && (
            <Alert severity="info" sx={{ mb: 2 }}>{t('sentSchedule.youOfflinePanel')}</Alert>
          )}
          {online && tabletOffline && lastSeen !== null && (
            <Alert severity="warning" icon={<CloudOff />} sx={{ mb: 2 }}>
              {t('sentSchedule.tabletOffline', { time: formatTime(lastSeen, lang, now) })}
            </Alert>
          )}
          <NameRow node={node} />
          {choice ? left : (
            <Box sx={{ display: 'grid', gridTemplateColumns: isPhone ? '1fr' : '1fr 400px', gap: 3 }}>
              {left}
              <StepList node={node} steps={steps} live pictures={pictures} schedules={schedules} />
            </Box>
          )}
          <Box sx={{ mt: 2, textAlign: isPhone ? 'center' : 'left' }}>
            <Button onClick={() => setSelectorOpen(true)} disabled={!online} sx={isPhone ? { minHeight: 48 } : undefined}>
              {t('sentSchedule.sendActionInstead')}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={removeOpen}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick' && guard.guarding()) return;
          closeRemove();
        }}
        maxWidth="xs"
        fullWidth
        aria-labelledby="remove-sent-title"
        slotProps={{ transition: { onEntering: () => cancelRef.current?.focus() } }}
      >
        <DialogTitle id="remove-sent-title">{t('sentSchedule.remove.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <RichText
              i18nKey={offlineWording ? 'sentSchedule.remove.bodyOffline' : 'sentSchedule.remove.body'}
              values={{ name: node.schedule?.name ?? '', device: deviceName, n, total }}
            />
          </DialogContentText>
          {offlineWording && (
            <DialogContentText variant="body2" sx={{ mt: 1.5 }}>
              {t('sentSchedule.remove.offlineFootnote')}
            </DialogContentText>
          )}
          {removeError && (
            <Alert severity="error" sx={{ mt: 2 }}>{t(`sentSchedule.remove.${removeError}`)}</Alert>
          )}
        </DialogContent>
        <DialogActions
          disableSpacing
          sx={isPhone
            ? { flexDirection: 'column', alignItems: 'stretch', gap: '12px', p: '8px 24px 24px' }
            : { gap: 1, px: 3, pb: 2 }}
        >
          <Button
            variant="outlined"
            color="error"
            disabled={removing}
            onPointerDown={guard.onPointerDown}
            onClick={confirmRemove}
            startIcon={removing ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{
              textTransform: 'none',
              minWidth: 0,
              ...(isPhone
                ? { minHeight: 48, alignSelf: 'flex-start', maxWidth: 'calc(100% - 32px)' }
                : { minHeight: 40 }),
            }}
          >
            {removing ? t('sentSchedule.remove.removing') : t('sentSchedule.remove.confirm')}
          </Button>
          <Button
            ref={cancelRef}
            variant="contained"
            autoFocus
            disabled={removing}
            onClick={closeRemove}
            sx={{ textTransform: 'none', ...(isPhone ? { minHeight: 48 } : { minHeight: 40, minWidth: 96 }) }}
          >
            {t('common.cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      <ActionSelector
        open={selectorOpen}
        onClose={() => setSelectorOpen(false)}
        deviceId={deviceId}
        actions={actions}
        onActionSelected={(actionId) => {
          const action = actions.find((a) => a.id === actionId);
          if (action) {
            setActionToSend(action);
            setSelectorOpen(false);
          }
        }}
        onReloadActions={onReloadActions}
        replacing
      />
      {actionToSend && (
        <SendActionDialog
          open
          onClose={() => setActionToSend(null)}
          deviceId={deviceId}
          action={actionToSend}
          replaces={{ kind: 'schedule', name: node.schedule?.name ?? '', n, total, device: deviceName }}
        />
      )}
    </>
  );
};
